/**
 * Endpoints de **Terminales POS** (`/api/terminales`).
 *
 * El listado, la edición y el estado se resuelven contra la tabla
 * `posidentifiers` (la misma que lee la app de POS); el **alta** la genera el
 * servicio de POS, así que la API le delega la llamada.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CatalogosTerminales,
  DetalleTerminal,
  Terminal,
  TerminalPayload,
  TerminalesRespuesta,
} from './terminales.types';

export const terminalesKeys = {
  lista: ['operaciones', 'terminales'] as const,
  catalogos: ['operaciones', 'terminales', 'catalogos'] as const,
  detalle: (codigo: string) => ['operaciones', 'terminales', codigo, 'detalle'] as const,
} as const;

export const terminalesApi = {
  listar: (signal?: AbortSignal): Promise<TerminalesRespuesta> =>
    apiClient.get<TerminalesRespuesta>('/api/terminales', { signal }),
  catalogos: (signal?: AbortSignal): Promise<CatalogosTerminales> =>
    apiClient.get<CatalogosTerminales>('/api/terminales/catalogos', { signal }),
  detalle: (codigo: string, signal?: AbortSignal): Promise<DetalleTerminal> =>
    apiClient.get<DetalleTerminal>(`/api/terminales/${codigo}/detalle`, { signal }),
  crear: (payload: TerminalPayload): Promise<{ terminal: Terminal | null; mensaje: string }> =>
    apiClient.post<{ terminal: Terminal | null; mensaje: string }>('/api/terminales', {
      body: payload,
    }),
  actualizar: (
    codigo: string,
    payload: TerminalPayload,
  ): Promise<{ terminal: Terminal; mensaje: string }> =>
    apiClient.put<{ terminal: Terminal; mensaje: string }>(`/api/terminales/${codigo}`, {
      body: payload,
    }),
  cambiarEstado: (codigo: string, activo: boolean): Promise<{ mensaje: string }> =>
    apiClient.patch<{ mensaje: string }>(`/api/terminales/${codigo}/estado`, { body: { activo } }),
};

/** Listado de terminales POS. */
export function useTerminales() {
  return useQuery({
    queryKey: terminalesKeys.lista,
    queryFn: ({ signal }) => terminalesApi.listar(signal),
  });
}

/** Catálogos del alta (tipos, métodos de pago y lados). */
export function useCatalogosTerminales() {
  return useQuery({
    queryKey: terminalesKeys.catalogos,
    queryFn: ({ signal }) => terminalesApi.catalogos(signal),
  });
}

/** Detalle de un terminal (lados y métodos de pago), sólo si se pide. */
export function useDetalleTerminal(codigo: string | null) {
  return useQuery({
    queryKey: terminalesKeys.detalle(codigo ?? ''),
    queryFn: ({ signal }) => terminalesApi.detalle(codigo ?? '', signal),
    enabled: codigo !== null,
  });
}

/** Alta, edición y estado de terminales. */
export function useMutarTerminales() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: terminalesKeys.lista });

  return {
    crear: useMutation({ mutationFn: terminalesApi.crear, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({ codigo, payload }: { codigo: string; payload: TerminalPayload }) =>
        terminalesApi.actualizar(codigo, payload),
      onSuccess: refrescar,
    }),
    cambiarEstado: useMutation({
      mutationFn: ({ codigo, activo }: { codigo: string; activo: boolean }) =>
        terminalesApi.cambiarEstado(codigo, activo),
      onSuccess: refrescar,
    }),
  };
}
